import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './EvaluationSessionModal.css';
import OpalStepper from '../../../OpalStepper/OpalStepper';
import EvaluationFactoryComponent from './EvaluationFactoryComponent/EvaluationFactoryComponent';
import { EvaluationFactoryData, EvaluationFactoryAnswerData } from './EvaluationFactoryData/EvaluationFactoryData';
import { getAll, getRecordById, updateRecord } from '../../../../localDB';
import appURL from '../../../../appURL.json';
import setting from '../../../../config.json';
import TauriLoader2 from '../../../TauriLoader2/TauriLoader2';
import { transformSyncEvaluationTemplate } from '../../../OpalSyncFactoryData/OpalSyncFactoryData';

const EvaluationSessionModal = ({ show, onHide, account, session, onFinishEvaluation, isFinishing }) => {
    const stepLabels = ['Fieldwork', 'Evaluation', 'Review', 'Submission'];
    const [currentStep, setCurrentStep] = useState(0);
    const [currentSubPage, setCurrentSubPage] = useState(0);
    const [maxSubPage, setMaxSubPage] = useState(0);
    const [prevMaxSubPages, setPrevMaxSubPages] = useState({});
    const [stepInComplete, setStepInComplete] = useState(false);
    const [subPageInComplete, setSubPageInComplete] = useState(false);
    const [factors, setFactors] = useState(null);
    const [answerFactor, setAnswerFactor] = useState(null);
    const [validationMessage, setValidationMessage] = useState('');
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleFinishEvaluation = () => {
        onFinishEvaluation(answerFactor, factors);
    };

    useEffect(() => {
        const loadFactors = async () => {
            try {
                const rawTemplate = await getAll("EvaluationTemplates");
                const template = rawTemplate?.[0] || null;

                setFactors(template?.TemplateJson);
            } catch (error) {
                console.error("Error loading template:", error);
            }
        };
        loadFactors();

        if (session) {
            setAnswerFactor(null)
            setCurrentStep(0);
            setCurrentSubPage(0);
            setMaxSubPage(0);
            setPrevMaxSubPages({});
        }
    }, [session]);

    const fetchGetEvaluationTemplate = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');
            const userGroupID = localStorage.getItem('usr_g');

            const response = await fetch(`${appURL.API_GetEvaluationTemplate}/${account?.UserGroupID ?? userGroupID}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                },
            });

            if (!response.ok) {
                return;
            }

            const result = await response.json();
            const templates = result.evaluation || [];
            const firstTemplate = templates[0];
            if (!firstTemplate) return;

            const existing = await getRecordById("EvaluationTemplates", Number(userGroupID));
            if (!existing) return;

            const updated = {
                ...existing,
                Description: firstTemplate.description,
                TemplateJson: firstTemplate.template,
                Version: firstTemplate.version
            };

            setFactors(firstTemplate.template);
            await updateRecord("EvaluationTemplates", updated);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && session) {
            fetchGetEvaluationTemplate();
            fetchAgentAccounts();
        }
    }, [show, session]);

    const fetchAgentAccounts = async () => {
        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');
            const response = await fetch(`${appURL.API_GetAgentSessionAccounts}/${session?.AccountID}/${session?.SessionDate}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            })

            if (!response.ok) {
                setClients([]);
                return;
            }

            const data = await response.json();
            setClients(data.accounts || []);
        } catch (error) {

        }
    }

    const formatted = useMemo(() => {
        if (!factors) return null;
        return EvaluationFactoryData(factors);
    }, [factors]);


    const initialAnswerFactor = useMemo(() => {
        if (!formatted) return null;
        return EvaluationFactoryAnswerData(formatted);
    }, [formatted]);


    useEffect(() => {
        if (!answerFactor && initialAnswerFactor) {
            setAnswerFactor(initialAnswerFactor);
        }
    }, [initialAnswerFactor]);

    const handleNext = () => {
        if (currentSubPage === maxSubPage && Number(currentStep) === 1) {
            const isGeneralEmpty = !answerFactor.general_evaluation?.trim();
            const isClientEmpty = !answerFactor.clients;

            const allFactorsIncomplete = answerFactor.factors.some(({ key, data }) => {

                const factorTemplate = factors?.factors.find(f => f.factor_id === key);

                const textItems = factorTemplate?.text_evaluations || [];
                const checklistGroups = factorTemplate?.checklist_evaluations || [];

                const isEmpty = (val) =>
                    val === null ||
                    val === undefined ||
                    (typeof val === "string" && val.trim() === "");

                const missingGrade = data.grade == null;

                const missingText =
                    textItems.length > 0 &&
                    textItems.some(({ id }) => {
                        const value = data?.text?.[id];
                        return isEmpty(value);
                    });

                const missingChecklist =
                    checklistGroups.length > 0 &&
                    !checklistGroups.some(group =>
                        group.items?.some(item =>
                            data?.checklist?.[item.id] === true
                        )
                    );

                return missingGrade || missingText || missingChecklist;
            });

            if (isClientEmpty || isGeneralEmpty || allFactorsIncomplete) {
                setValidationMessage('Complete the details first before review!');
                setSubPageInComplete(true);
                return;
            } else {
                setValidationMessage('');
                setStepInComplete(false);
            }
        }

        if (subPageInComplete) {
            return; // Block navigation if there's an error
        }

        if (currentSubPage < maxSubPage) {
            setCurrentSubPage(prev => prev + 1);
        } else if (currentStep < stepLabels.length - 1) {
            setPrevMaxSubPages(prev => ({
                ...prev,
                [currentStep]: maxSubPage
            }));
            setCurrentStep(prev => prev + 1);
            setCurrentSubPage(0);
            setMaxSubPage(0);
        }
    };

    const handleBack = () => {
        if (currentSubPage > 0) {
            setCurrentSubPage(prev => prev - 1);
        } else if (currentStep > 0) {
            const previousStep = currentStep - 1;
            const savedMax = prevMaxSubPages[previousStep] ?? 0;

            setCurrentStep(prev => prev - 1);
            setCurrentSubPage(savedMax);
        }
    };

    if (!factors || !answerFactor || !show || !session) {
        return null; // or a loading spinner
    }

    return (
        <Modal
            className='evaluation-session-modal'
            show={show}
            onHide={onHide}
            size="lg"
            backdrop={"static"}  // Prevent closing on outside click
            keyboard={false}
            centered>
            <Modal.Header>
                <span className='text-nowrap'>Joint Call Evaluation</span>
                <OpalStepper
                    steps={stepLabels}
                    step={currentStep}
                    activeColor={"#FFC107"}
                />
            </Modal.Header>
            <Modal.Body>
                {loading ? <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" /> :
                    <EvaluationFactoryComponent
                        account={account}
                        session={session}
                        clients={clients}
                        factor={formatted}
                        type={stepLabels[currentStep]}
                        answerFactor={answerFactor}
                        currentStep={currentStep}
                        currentSubPage={currentSubPage}
                        maxSubPage={maxSubPage}
                        setCurrentStep={setCurrentStep}
                        setCurrentSubPage={setCurrentSubPage}
                        setMaxSubPage={setMaxSubPage}
                        setAnswerFactor={setAnswerFactor}
                        setStepInComplete={setStepInComplete}
                        setSubPageInComplete={setSubPageInComplete}
                        validationMessage={validationMessage}
                        setValidationMessage={setValidationMessage}
                    />
                }
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex w-100 justify-content-between align-items-center gap-2">
                    <div className="flex-grow-1">
                        {subPageInComplete && (
                            <div className="text-danger text-nowrap">
                                {validationMessage}
                            </div>
                        )}
                    </div>
                    <div className='d-flex gap-2'>
                        <Button onClick={handleBack} className="btn-evaluation-back d-flex">
                            <span className="text-nowrap">Back</span>
                        </Button>
                        {(currentStep === stepLabels.length - 1 && currentSubPage === maxSubPage) ? (
                            <Button className='btn-evaluation-next text-nowrap d-flex' onClick={handleFinishEvaluation} disabled={isFinishing || subPageInComplete}>
                                {isFinishing && (
                                    <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.2rem" color="#FFF" />
                                )}
                                <span className="ms-2 text-nowrap">Finish</span>
                            </Button>
                        ) : (
                            <Button onClick={handleNext} className="btn-evaluation-next d-flex">
                                <span className="text-nowrap">Next</span>
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default EvaluationSessionModal;
